import React, { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Heart, Activity, Droplets, Info, Calculator, Stethoscope, Scale } from 'lucide-react';

const ChallengeSection = () => {
  const { t } = useLanguage();

  const [height, setHeight] = React.useState('');
  const [weight, setWeight] = React.useState('');
  const [bmi, setBmi] = React.useState('');

  useEffect(() => {
    const h = parseFloat(height);
    const w = parseFloat(weight);
    setBmi(w > 0 && h > 0 ? (w / (h * h)).toFixed(1) : '');
  }, [weight, height]);

  const bmiCategories = [
    { label: 'Underweight', range: '< 18.5', color: 'bg-blue-100 text-blue-700', min: 0, max: 18.4 },
    { label: 'Normal', range: '18.5–24.9', color: 'bg-green-100 text-green-700', min: 18.5, max: 24.9 },
    { label: 'Overweight', range: '25.0–29.9', color: 'bg-yellow-100 text-yellow-700', min: 25, max: 29.9 },
    { label: 'Obesity I', range: '30.0–34.9', color: 'bg-orange-100 text-orange-700', min: 30, max: 34.9 },
    { label: 'Obesity II', range: '35.0–39.9', color: 'bg-red-100 text-red-700', min: 35, max: 39.9 },
    { label: 'Obesity III', range: '≥ 40.0', color: 'bg-purple-100 text-purple-700', min: 40, max: 100 },
  ];

  // Logic to find the active category for highlighting
  const activeCategory = bmi ? bmiCategories.find(c => parseFloat(bmi) >= c.min && parseFloat(bmi) <= c.max) : null;

  return (
    <section id="challenge" className="py-20 bg-[#F8FAFC]" data-testid="challenge-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-bold tracking-widest text-[#800000] uppercase  rounded-full">
            {t('challenge_subtitle')}
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight">
            {t('challenge_title')}
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed p-2">
            {t('challenge_description')}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-stretch">
          
          {/* Left: Clinical Info & Redesigned Calculator */}
          <div className="lg:col-span-7 space-y-8">
            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <Stethoscope className="text-blue-600 w-6 h-6" />
                <h3 className="text-2xl font-bold text-slate-900">Clinical Evaluation</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-8 text-sm">
                <div>
                  <h4 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    Why Evaluate?
                  </h4>
                  <p className="text-slate-600 leading-relaxed">
                    Obesity is a complex metabolic condition. A professional assessment identifies 
                    risks like Type 2 diabetes and hypertension before they progress.
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    The Process
                  </h4>
                  <p className="text-slate-600 leading-relaxed">
                    Includes a physical exam, lipid panel blood tests, and a review of metabolic 
                    markers with your primary care physician.
                  </p>
                </div>
              </div>
            </div>

            {/* REDESIGNED CALCULATOR PART */}
            <div className="bg-slate-900 rounded-3xl p-1 shadow-2xl shadow-blue-900/20">
              <div className="bg-slate-800/50 rounded-[22px] p-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                  <div>
                    <div className="flex items-center gap-2 text-blue-400 mb-1">
                      <Calculator className="w-5 h-5" />
                      <span className="text-xs font-bold uppercase tracking-wider">Health Tool</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white">BMI Calculator</h3>
                  </div>
                  
                  <div className="flex items-center gap-4 bg-slate-900/50 p-2 rounded-2xl border border-white/5">
                    <div className="text-right">
                      <p className="text-[10px] text-slate-400 uppercase font-bold px-2">Your Result</p>
                      <div className="text-3xl font-mono font-black text-white px-2">
                        {bmi || '--.-'}
                      </div>
                    </div>
                    {activeCategory && (
                      <div className={`px-4 py-2 rounded-xl text-xs font-bold transition-all duration-500 ${activeCategory.color}`}>
                        {activeCategory.label}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 uppercase ml-1">Weight (kg)</label>
                    <div className="relative">
                      <Scale className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        type="number"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                        placeholder="00"
                        className="w-full pl-12 pr-4 py-4 bg-slate-900 border border-slate-700 rounded-2xl text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder:text-slate-700"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-400 uppercase ml-1">Height (meters)</label>
                    <div className="relative">
                      <Activity className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        type="number"
                        value={height}
                        onChange={(e) => setHeight(e.target.value)}
                        placeholder="1.75"
                        className="w-full pl-12 pr-4 py-4 bg-slate-900 border border-slate-700 rounded-2xl text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder:text-slate-700"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-2 pt-2 border-t border-white/5">
                 <div className="">
                   <div className="flex items-center gap-2 text-slate-500 text-xs italic">
                     <Info className="w-3.5 h-3.5" />
                     <span> Body Mass Index is a universal screening tool using height and weight to estimate body fat levels.</span>
                   </div>
                </div>
                   <div className="flex items-center gap-2 text-slate-500 text-xs italic">
                     <Info className="w-3.5 h-3.5" />
                     <span>Formula: Weight / (Height × Height)</span>
                   </div>
                   
                </div>
              </div>
            </div>
          </div>

          {/* Right: BMI Table */}
          <div className="lg:col-span-5">
            <div className="bg-white h-98 rounded-3xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
              <div className="p-4 border-b border-slate-50 flex items-center justify-between">
                <h3 className="font-bold text-slate-900 text-lg">Severity Grades</h3>
                <Info className="w-4 h-4 text-slate-400" />
              </div>
              <div className="divide-y divide-slate-50 flex-grow">
                {bmiCategories.map((cat, idx) => {
                  const isActive = activeCategory?.label === cat.label;
                  return (
                    <div 
                      key={idx} 
                      className={`flex items-center justify-between p-4 transition-all duration-300 ${isActive ? 'bg-slate-50 scale-[1.02] z-10 shadow-sm' : 'opacity-70'}`}
                    >
                      <div className="flex items-center gap-3">
                        {isActive && <div className="w-1.5 h-6 bg-blue-600 rounded-full" />}
                        <span className={`px-3 py-1 rounded-lg text-xs font-bold uppercase ${cat.color}`}>
                          {cat.label}
                        </span>
                      </div>
                      <span className={`font-mono text-sm ${isActive ? 'text-slate-900 font-bold' : 'text-slate-500'}`}>
                        {cat.range}
                      </span>
                      
                    </div>
                    
                  );
                })}
              </div>
              
                {/* <p className="text-[10px] text-slate-800 text-center leading-relaxed italic">
                  
                  Body Mass Index is a universal screening tool using height and weight to estimate body fat levels.
              </p> */}
              
              <div className="p-2 bg-slate-50">
            
                <p className="text-[11px] text-slate-400 text-center leading-relaxed italic">
                  Note: BMI is a general indicator and does not account for muscle mass, bone density, or overall body composition.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer section */}
        <footer className="mt-20 text-center">
          <style>
            {`
              @keyframes autoscale {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.03); }
              }
              .animate-autoscale {
                animation: autoscale 4s ease-in-out infinite;
              }
            `}
          </style>

          <div className="animate-autoscale inline-block bg-white p-8 md:p-12 rounded-[40px] border border-slate-100 shadow-xl max-w-4xl">
            <h1 className='text-3xl md:text-4xl mb-8 text-[#800000] font-black tracking-tight'>
              Who should consult us?
            </h1>
            
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-4 text-left">
              {[
                "Difficulty losing weight despite efforts",
                "Weight regain after dieting",
                "Obesity with diabetes / fatty liver",
                "Considering bariatric surgery",
                "Looking for medically supervised weight loss"
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-blue-600 shrink-0" />
                  <span className="font-bold text-slate-700 text-sm md:text-base leading-tight">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </footer>
      </div>
    </section>
  );
};

export default ChallengeSection;